import React, { useCallback, useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { supabase } from "../supabaseClient";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  SUPPORT_TICKET_SOURCES,
  WEBSITE_ISSUE_CATEGORIES,
  createSupportTicket,
  groupSupportTicketsByTab,
  isRefundRequestType,
  listMySupportTicketsPage,
  supportTicketSubject,
} from "../lib/supportTickets";
import { listBuyerPaidOrdersForRefund } from "../lib/refundOrders";
import { buildOrderCode } from "../admin/ordersPage";
import ChangeNamePanel from "./profile/ChangeNamePanel";
import CurrentProfilePanel from "./profile/CurrentProfilePanel";
import SecurityPanel from "./profile/SecurityPanel";
import TermsPolicyContent from "../components/TermsPolicyContent";

const formatSupportDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-PH", {
    timeZone: "Asia/Manila",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const Profile = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState("profile");
  const [profileView, setProfileView] = useState("overview");
  const [issueCategory, setIssueCategory] = useState("Ticket Issue");
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [refundOrders, setRefundOrders] = useState([]);
  const [refundOrdersLoading, setRefundOrdersLoading] = useState(false);
  const [refundOrdersError, setRefundOrdersError] = useState("");
  const [supportSubject, setSupportSubject] = useState("");
  const [supportDescription, setSupportDescription] = useState("");
  const [supportSubmitting, setSupportSubmitting] = useState(false);
  const [supportMessage, setSupportMessage] = useState("");
  const [supportError, setSupportError] = useState("");
  const [myTickets, setMyTickets] = useState([]);
  const [ticketsPage, setTicketsPage] = useState(1);
  const [ticketsHasMore, setTicketsHasMore] = useState(false);
  const [ticketsLoadingMore, setTicketsLoadingMore] = useState(false);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsError, setTicketsError] = useState("");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const loadMyTickets = useCallback(async ({ append = false } = {}) => {
    if (!append) setTicketsLoading(true);
    else setTicketsLoadingMore(true);
    setTicketsError("");
    try {
      const page = append ? ticketsPage + 1 : 1;
      const { rows, hasMore } = await listMySupportTicketsPage({ page, pageSize: 10 });
      setMyTickets((prev) => (append ? [...prev, ...rows.filter((row) => !prev.some((item) => item.id === row.id))] : rows));
      setTicketsPage(page);
      setTicketsHasMore(hasMore);
    } catch (error) {
      setTicketsError(error.message || "Unable to load your support tickets.");
      if (!append) setMyTickets([]);
    } finally {
      setTicketsLoading(false);
      setTicketsLoadingMore(false);
    }
  }, [ticketsPage]);

  const loadRefundOrders = useCallback(async () => {
    setRefundOrdersLoading(true);
    setRefundOrdersError("");
    try {
      const rows = await listBuyerPaidOrdersForRefund();
      setRefundOrders(rows);
    } catch (error) {
      setRefundOrders([]);
      setRefundOrdersError(error.message || "Unable to load your paid orders.");
    } finally {
      setRefundOrdersLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        localStorage.removeItem("userAuthenticated");
        setLoading(false);
        navigate("/login", { replace: true });
        return;
      }

      const { data, error } = await supabase
        .from("users")
        .select("*")
        .eq("id", user.id)
        .single();

      if (error) {
        console.error("Error fetching user data:", error);
        setUserData({
          id: user.id,
          full_name: user.user_metadata?.full_name || user.user_metadata?.name || "",
          email: user.email || "",
        });
      } else {
        setUserData({
          ...data,
          id: data?.id || user.id,
          full_name: data?.full_name || user.user_metadata?.full_name || user.user_metadata?.name || "",
          email: data?.email || user.email || "",
        });
      }

      setLoading(false);
    };

    fetchUserData();
  }, [navigate]);

  useEffect(() => {
    const section = String(searchParams.get("section") || "").trim().toLowerCase();
    if (section === "support") {
      setActiveSection("support");
    }
  }, [searchParams]);

  useEffect(() => {
    if (activeSection === "support" && userData) {
      loadMyTickets();
    }
  }, [activeSection, loadMyTickets, userData]);

  useEffect(() => {
    if (activeSection === "support" && isRefundRequestType(issueCategory) && userData) {
      loadRefundOrders();
    }
  }, [activeSection, issueCategory, loadRefundOrders, userData]);

  const groupedTickets = useMemo(() => groupSupportTicketsByTab(myTickets), [myTickets]);

  const handleCategoryChange = (value) => {
    setIssueCategory(value);
    if (!isRefundRequestType(value)) {
      setSelectedOrderId("");
      setRefundOrdersError("");
    }
  };

  const handleSupportSubmit = async (event) => {
    event.preventDefault();

    const subject = supportSubject.trim();
    const description = supportDescription.trim();

    if (!issueCategory) {
      setSupportError("Please select an issue category.");
      setSupportMessage("");
      return;
    }

    if (isRefundRequestType(issueCategory) && !selectedOrderId) {
      setSupportError("Please select the order you want refunded.");
      setSupportMessage("");
      return;
    }

    if (!subject) {
      setSupportError("Please enter a subject.");
      setSupportMessage("");
      return;
    }

    if (!description) {
      setSupportError("Please describe your issue.");
      setSupportMessage("");
      return;
    }

    setSupportSubmitting(true);
    setSupportError("");
    setSupportMessage("");

    try {
      const wasRefund = isRefundRequestType(issueCategory);
      const created = await createSupportTicket({
        issueType: issueCategory,
        subject,
        description,
        source: SUPPORT_TICKET_SOURCES.WEBSITE,
        paymentOrderId: wasRefund ? selectedOrderId : null,
      });
      setSupportSubject("");
      setSupportDescription("");
      setSelectedOrderId("");
      setSupportMessage(
        wasRefund
          ? "Your refund request was submitted. We will email you when it is approved or denied. You can open this ticket below anytime."
          : "Your support ticket was submitted. You can open it below anytime."
      );
      await loadMyTickets();
      if (wasRefund) {
        await loadRefundOrders();
      }
      if (created) {
        setSelectedTicket(created);
      }
    } catch (error) {
      setSupportError(error.message || "Unable to submit this ticket.");
    } finally {
      setSupportSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    localStorage.removeItem("userAuthenticated");
    navigate("/login");
  };

  const handleSectionChange = (section) => {
    setActiveSection(section);
    if (section === "profile") {
      setProfileView("overview");
    }
  };

  const handleProfileUpdated = (nextProfile) => {
    setUserData((prev) => ({
      ...prev,
      ...nextProfile,
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#121212] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-brand-maroon border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#121212] text-white pt-8 sm:pt-12 pb-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 min-w-0">

        {/* PAGE TITLE */}
        <h1 className="text-lg font-semibold pb-4 mb-6 border-b border-white/10">My Account</h1>

        <div className="flex flex-col md:flex-row gap-6 md:gap-10 min-w-0">

          {/* LEFT MENU — horizontal scroll tabs on mobile, sidebar on md+ */}
          <aside className="w-full md:w-56 shrink-0 bg-[#1a1a1a] rounded-md p-3 sm:p-4 h-fit min-w-0">
            <nav className="flex md:flex-col gap-1 text-sm overflow-x-auto no-scrollbar md:overflow-visible">
              <button
                type="button"
                onClick={() => handleSectionChange("profile")}
                className={`shrink-0 md:w-full text-left px-3 py-2.5 min-h-10 rounded whitespace-nowrap ${
                  activeSection === "profile"
                    ? "bg-brand-maroon text-white font-medium"
                    : "text-gray-400 hover:bg-white/5"
                }`}
              >
                My Profile
              </button>
              <button
                type="button"
                onClick={() => handleSectionChange("support")}
                className={`shrink-0 md:w-full text-left px-3 py-2.5 min-h-10 rounded whitespace-nowrap ${
                  activeSection === "support"
                    ? "bg-brand-maroon text-white font-medium"
                    : "text-gray-400 hover:bg-white/5"
                }`}
              >
                Support
              </button>
              <button
                type="button"
                onClick={() => handleSectionChange("terms")}
                className={`shrink-0 md:w-full text-left px-3 py-2.5 min-h-10 rounded whitespace-nowrap ${
                  activeSection === "terms"
                    ? "bg-brand-maroon text-white font-medium"
                    : "text-gray-400 hover:bg-white/5"
                }`}
              >
                Terms & Policy
              </button>
              <button
                type="button"
                onClick={handleLogout}
                className="shrink-0 md:w-full text-left px-3 py-2.5 min-h-10 rounded whitespace-nowrap text-gray-400 hover:bg-white/5 md:mt-4"
              >
                Logout
              </button>
            </nav>
          </aside>

          {/* MAIN CONTENT */}
          <section className="flex-1 min-w-0">

            {activeSection === "terms" && (
              <div className="max-w-4xl min-w-0">
                <h2 className="text-xs font-semibold tracking-widest text-gray-300 mb-5">
                  TERMS & POLICY
                </h2>
                <TermsPolicyContent
                  showRefundPolicy
                  onSupportClick={() => handleSectionChange("support")}
                  supportLinkLabel="Supports Page"
                />
              </div>
            )}

            {activeSection === "support" && (
              <div className="max-w-4xl min-w-0">
                <h2 className="text-xs font-semibold tracking-widest text-gray-300 mb-5">
                  SUPPORT
                </h2>

                <div className="bg-[#1a1a1a] border border-white/10 rounded-md p-4 sm:p-6">
                  <h3 className="text-lg font-semibold text-white mb-6">
                    Submit a Support Ticket
                  </h3>

                  <form className="space-y-5" onSubmit={handleSupportSubmit}>
                    <div>
                      <label
                        htmlFor="issue-category"
                        className="block text-sm text-gray-200 mb-2"
                      >
                        Issue Category
                      </label>
                      <select
                        id="issue-category"
                        value={issueCategory}
                        onChange={(event) => handleCategoryChange(event.target.value)}
                        className="w-full h-10 rounded bg-[#121212] border border-white/10 text-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-brand-maroon appearance-none bg-no-repeat bg-[length:16px] bg-[position:right_8px_center]"
                        style={{
                          backgroundImage: "url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%239ca3af%22 stroke-width=%222%22%3e%3cpolyline points=%226 9 12 15 18 9%22%3e%3c/polyline%3e%3c/svg%3e')",
                        }}
                      >
                        {WEBSITE_ISSUE_CATEGORIES.map((category) => (
                          <option key={category} className="bg-[#121212] text-gray-200" value={category}>
                            {category}
                          </option>
                        ))}
                      </select>
                    </div>

                    {isRefundRequestType(issueCategory) ? (
                      <div>
                        <label
                          htmlFor="refund-order"
                          className="block text-sm text-gray-200 mb-2"
                        >
                          Order to refund
                        </label>
                        <select
                          id="refund-order"
                          value={selectedOrderId}
                          onChange={(event) => setSelectedOrderId(event.target.value)}
                          disabled={refundOrdersLoading || refundOrders.length === 0}
                          className="w-full h-10 rounded bg-[#121212] border border-white/10 text-gray-200 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-brand-maroon appearance-none bg-no-repeat bg-[length:16px] bg-[position:right_8px_center] disabled:opacity-60"
                          style={{
                            backgroundImage: "url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%239ca3af%22 stroke-width=%222%22%3e%3cpolyline points=%226 9 12 15 18 9%22%3e%3c/polyline%3e%3c/svg%3e')",
                          }}
                        >
                          <option className="bg-[#121212] text-gray-200" value="">
                            {refundOrdersLoading
                              ? "Loading paid orders..."
                              : refundOrders.length === 0
                                ? "No paid orders available for refund"
                                : "Select a paid order"}
                          </option>
                          {refundOrders.map((order) => (
                            <option key={order.id} className="bg-[#121212] text-gray-200" value={order.id}>
                              {order.label}
                            </option>
                          ))}
                        </select>
                        {refundOrdersError ? (
                          <p className="text-xs text-red-400 mt-2">{refundOrdersError}</p>
                        ) : (
                          <p className="text-xs text-gray-400 mt-2">
                            Choose the paid order you want refunded. An admin will review your request.
                          </p>
                        )}
                      </div>
                    ) : null}

                    <div>
                      <label
                        htmlFor="support-subject"
                        className="block text-sm text-gray-200 mb-2"
                      >
                        Subject
                      </label>
                      <input
                        id="support-subject"
                        type="text"
                        value={supportSubject}
                        onChange={(event) => setSupportSubject(event.target.value)}
                        placeholder="e.g. Didn’t receive my ticket"
                        className="w-full h-10 rounded bg-[#121212] border border-white/10 text-gray-200 placeholder:text-gray-500 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-brand-maroon"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="support-description"
                        className="block text-sm text-gray-200 mb-2"
                      >
                        Description
                      </label>
                      <textarea
                        id="support-description"
                        rows={6}
                        value={supportDescription}
                        onChange={(event) => setSupportDescription(event.target.value)}
                        className="w-full rounded bg-[#121212] border border-white/10 text-gray-200 placeholder:text-gray-500 px-3 py-2 text-sm resize-y focus:outline-none focus:ring-1 focus:ring-brand-maroon"
                      />
                      <p className="text-xs text-gray-400 mt-2">
                        Please describe your issue in detail. Include event name, date, and what went wrong.
                      </p>
                    </div>

                    {supportError ? (
                      <p className="text-sm text-red-400">{supportError}</p>
                    ) : null}
                    {supportMessage ? (
                      <p className="text-sm text-emerald-400">{supportMessage}</p>
                    ) : null}

                    <div className="pt-1">
                      <button
                        type="submit"
                        disabled={
                          supportSubmitting
                          || (isRefundRequestType(issueCategory)
                            && (refundOrdersLoading || !selectedOrderId || refundOrders.length === 0))
                        }
                        className="w-full sm:w-auto px-6 py-2.5 min-h-10 bg-brand-maroon rounded text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                      >
                        {supportSubmitting ? "Submitting..." : "Submit"}
                      </button>
                    </div>
                  </form>
                </div>

                <div className="bg-[#1a1a1a] border border-white/10 rounded-md p-4 sm:p-6 mt-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Your tickets</h3>
                  {ticketsLoading && myTickets.length === 0 ? (
                    <p className="text-sm text-gray-400">Loading...</p>
                  ) : ticketsError ? (
                    <p className="text-sm text-red-400">{ticketsError}</p>
                  ) : myTickets.length === 0 ? (
                    <p className="text-sm text-gray-400">You have not submitted any support tickets yet.</p>
                  ) : (
                    <div className="space-y-5">
                      <div>
                        <h4 className="text-xs font-semibold tracking-widest text-gray-400 mb-2">ONGOING</h4>
                        {groupedTickets.ongoing.length === 0 ? (
                          <p className="text-sm text-gray-500">No ongoing tickets.</p>
                        ) : (
                          <ul className="space-y-2">
                            {groupedTickets.ongoing.map((ticket) => (
                              <li key={ticket.id}>
                                <button
                                  type="button"
                                  onClick={() => setSelectedTicket(ticket)}
                                  className="w-full rounded bg-[#121212] border border-white/10 px-3 py-2 text-left hover:border-brand-maroon/50 hover:bg-white/[0.03]"
                                >
                                  <p className="text-sm text-white">{ticket.ticket_code} · {ticket.status}</p>
                                  <p className="text-xs text-gray-400">{supportTicketSubject(ticket)}</p>
                                  <p className="text-[10px] text-gray-500 mt-1">Tap to view details</p>
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div>
                        <h4 className="text-xs font-semibold tracking-widest text-gray-400 mb-2">RESOLVED</h4>
                        {groupedTickets.closed.length === 0 ? (
                          <p className="text-sm text-gray-500">No resolved tickets.</p>
                        ) : (
                          <ul className="space-y-2">
                            {groupedTickets.closed.map((ticket) => (
                              <li key={ticket.id}>
                                <button
                                  type="button"
                                  onClick={() => setSelectedTicket(ticket)}
                                  className="w-full rounded bg-[#121212] border border-white/10 px-3 py-2 text-left hover:border-brand-maroon/50 hover:bg-white/[0.03]"
                                >
                                  <p className="text-sm text-white">{ticket.ticket_code} · {ticket.status}</p>
                                  <p className="text-xs text-gray-400">{supportTicketSubject(ticket)}</p>
                                  <p className="text-[10px] text-gray-500 mt-1">Tap to view details</p>
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      {ticketsHasMore ? (
                        <button
                          type="button"
                          disabled={ticketsLoadingMore}
                          onClick={() => loadMyTickets({ append: true })}
                          className="min-w-[140px] w-full sm:w-auto px-6 py-2 rounded bg-brand-maroon text-white text-sm font-semibold disabled:opacity-40 disabled:pointer-events-none"
                        >
                          {ticketsLoadingMore ? "Loading..." : "Load More"}
                        </button>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            )}

            {selectedTicket ? (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
                <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl border border-white/10 bg-[#1a1a1a] p-5 sm:p-6 shadow-2xl">
                  <button
                    type="button"
                    onClick={() => setSelectedTicket(null)}
                    className="absolute top-4 right-4 text-gray-400 hover:text-white"
                    aria-label="Close ticket details"
                  >
                    <X size={18} />
                  </button>

                  <h3 className="pr-8 text-lg font-semibold text-white">Ticket details</h3>
                  <p className="mt-1 text-xs text-gray-400">{selectedTicket.ticket_code}</p>

                  <dl className="mt-5 space-y-4 text-sm">
                    <div>
                      <dt className="text-xs text-gray-500 mb-1">Status</dt>
                      <dd className="text-white">{selectedTicket.status}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500 mb-1">Issue type</dt>
                      <dd className="text-white">{selectedTicket.issue_type}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500 mb-1">Subject</dt>
                      <dd className="text-white">{supportTicketSubject(selectedTicket)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500 mb-1">Details</dt>
                      <dd className="whitespace-pre-wrap break-words text-gray-200">{selectedTicket.description}</dd>
                    </div>
                    {isRefundRequestType(selectedTicket.issue_type) && selectedTicket.payment_order_id ? (
                      <div>
                        <dt className="text-xs text-gray-500 mb-1">Linked order</dt>
                        <dd className="text-white">{buildOrderCode(selectedTicket.payment_order_id)}</dd>
                        <p className="text-xs text-gray-400 mt-1">
                          After approval, refunded event tickets appear under My Tickets → Refunded.
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedTicket(null);
                            navigate("/tickets");
                          }}
                          className="mt-2 text-xs font-semibold text-brand-maroon hover:underline"
                        >
                          Open My Tickets
                        </button>
                      </div>
                    ) : null}
                    {selectedTicket.admin_notes ? (
                      <div>
                        <dt className="text-xs text-gray-500 mb-1">Admin update</dt>
                        <dd className="whitespace-pre-wrap break-words rounded border border-white/10 bg-[#121212] px-3 py-2 text-gray-200">
                          {selectedTicket.admin_notes}
                        </dd>
                      </div>
                    ) : null}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-gray-400">
                      <div>
                        <p className="mb-1 text-gray-500">Submitted</p>
                        <p>{formatSupportDate(selectedTicket.created_at)}</p>
                      </div>
                      <div>
                        <p className="mb-1 text-gray-500">Last updated</p>
                        <p>{formatSupportDate(selectedTicket.updated_at)}</p>
                      </div>
                    </div>
                  </dl>

                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setSelectedTicket(null)}
                      className="rounded bg-white/10 px-4 py-2 text-xs font-semibold text-white hover:bg-white/15"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {activeSection === "profile" && (
              profileView === "change-name" ? (
                <ChangeNamePanel
                  userId={userData?.id}
                  userEmail={userData?.email || ""}
                  currentName={userData?.full_name || ""}
                  onBack={() => setProfileView("overview")}
                  onNameUpdated={(full_name) => handleProfileUpdated({ full_name })}
                />
              ) : (
                <div className="w-full min-w-0 space-y-10">
                  <CurrentProfilePanel
                    userId={userData?.id}
                    userName={userData?.full_name || ""}
                    userEmail={userData?.email || ""}
                    onNameClick={() => setProfileView("change-name")}
                    onProfileUpdated={handleProfileUpdated}
                  />
                  <SecurityPanel />
                </div>
              )
            )}

          </section>
        </div>
      </div>
    </div>
  );
};

export default Profile;